import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  inject,
} from '@angular/core';
import {
  GlobalActions,
  GlobalEventDispatcher,
  ModalCloseReason,
  ModalData,
} from '@ps20-frontend/shop-integration-redux';
import { NgRedux, select } from '@angular-redux3/store';
import { Observable, Subscription, merge, timer } from 'rxjs';
import { filter } from 'rxjs/operators';
import { App } from '@ps20-frontend/frontend-models';
import {
  NxtSpinnerOverlayModule,
  NxtSpinnerOverlayService,
} from '@nxt/ngx-ndbx-extensions/spinner-overlay';
import { ModalButtonType, ModalComponent } from '@ps20-frontend/gui-components';
import stickybits from 'stickybits';
import { OAuthService } from '@angular-redux3/angular-oauth2-oidc';
import { LazyElementDirective } from '@angular-extensions/elements';
import { AsyncPipe } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { NxCopytextComponent } from '@allianz/ng-aquila/copytext';
import { HtmlPipe } from '@ps20-frontend/text-service';
import { IAppState, localStore as shopStore } from '../store/localStore';
import { loadAppConfig } from '../store/local.actions';
import { logAppInitializedConditions } from '../portal';
import {
  isApigeeActiveAndNotPermasession,
  isSdwSession,
  selectConstructionPageOpen,
  selectErrorpageIsOpen,
  selectForwardedToUnderwriterPageOpen,
  selectSessionExpired,
  selectUserLoggedOut,
  selectVnAddressInvalidPageOpen,
} from '../store/selectors/transientState.selector';
import { DesignContainerComponent } from './design-container/design-container.component';
import { ErrorpageComponent } from './errorpage/errorpage.component';
import { SessionExpiredPageComponent } from './session-expired-page/session-expired-page.component';
import { LogoutpageComponent } from './logoutpage/logoutpage.component';
import { ConstructionpageComponent } from './constructionpage/constructionpage.component';
import { ForwardedToUnderwriterPageComponent } from './forwarded-to-underwriter-page/forwarded-to-underwriter-page.component';
import { VnAddressInvalidComponent } from './vn-address-invalid/vn-address-invalid.component';
import { DevMockPanelComponent } from './dev-mock/dev-mock-panel.component';

export const selectAppIsInitialized = (state: IAppState): boolean =>
  state.transientState.appIsInitialized;
export const selectMainFilenameToolbar = (app: IAppState): string =>
  app.transientState.appSourcePathConfigs[App.SHOP_TOOLBAR];
export const selectModalData = (state: IAppState): ModalData => state.transientState.modal;

const STICKY_OFFSET = -60;

@Component({
  selector: 'ps20-shop-root',
  templateUrl: './app.component.html',
  imports: [
    DesignContainerComponent,
    NxtSpinnerOverlayModule,
    NxCopytextComponent,
    HtmlPipe,
    AsyncPipe,
    LazyElementDirective,
    RouterOutlet,
    ModalComponent,
    ErrorpageComponent,
    SessionExpiredPageComponent,
    LogoutpageComponent,
    ConstructionpageComponent,
    ForwardedToUnderwriterPageComponent,
    VnAddressInvalidComponent,
    DevMockPanelComponent,
  ],
})
export class AppComponent implements AfterViewInit, OnDestroy, OnInit {
  private readonly ngRedux = inject<NgRedux<IAppState>>(NgRedux);
  private readonly spinnerService = inject(NxtSpinnerOverlayService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly globalActions = inject(GlobalActions);
  private readonly oauthService = inject(OAuthService);

  @HostBinding('attr.data-cy') readonly dataCy = 'ps20-shop';
  @select(selectAppIsInitialized) appIsInitialized$: Observable<boolean>;
  @select(selectMainFilenameToolbar) mainFilenameToolbar$: Observable<string>;
  @select(selectErrorpageIsOpen) errorpageIsOpen$: Observable<boolean>;
  @select(selectUserLoggedOut) userloggedout$: Observable<boolean>;
  @select(selectSessionExpired) sessionExpired$: Observable<boolean>;
  @select(selectConstructionPageOpen)
  constructionpageIsOpen$: Observable<boolean>;
  @select(selectForwardedToUnderwriterPageOpen)
  forwardedToUnderwriterPageOpen$: Observable<boolean>;
  @select(selectVnAddressInvalidPageOpen)
  vnAddressInvalidPageOpen$: Observable<boolean>;
  @select(selectModalData) modalData$: Observable<ReturnType<typeof selectModalData>>;

  appIsInitializedSubscription: Subscription;

  spinnerIsActive = false;
  readonly showDevMockPanel = location.host.includes('localhost');

  @ViewChild(TemplateRef, { static: false }) waitingpage: TemplateRef<unknown>;

  globalEventDispatcher: GlobalEventDispatcher = GlobalEventDispatcher.getInstance();

  private timer: Observable<number>;

  constructor() {
    const viewContainerRef = inject(ViewContainerRef);
    const spinnerService = this.spinnerService;

    spinnerService.viewContainerRef = viewContainerRef;
    this.ngRedux.dispatch(loadAppConfig(App.SHOP_TOOLBAR));
  }

  ngOnInit(): void {
    shopStore.bindApp(this.changeDetectorRef);
    if (
      isSdwSession(this.ngRedux.getState()) ||
      isApigeeActiveAndNotPermasession(this.ngRedux.getState())
    ) {
      // update every 14 minutes the accessToken
      this.timer = timer(840000, 840000);
      this.timer.subscribe(() => this.onTimeOut());
    }
  }

  onTimeOut(): void {
    this.oauthService.refreshToken().then(response => {
      this.globalEventDispatcher.dispatch(this.globalActions.setAccessToken(response.access_token));
    });
  }

  shouldShowToolbar(location: Location): boolean {
    return !location.hash.includes('checkout/danke');
  }

  /* istanbul ignore next */
  getLocation(): Location {
    return location;
  }

  ngAfterViewInit(): void {
    stickybits('#toolbar', { stickyBitStickyOffset: STICKY_OFFSET });

    this.appIsInitializedSubscription = this.appIsInitialized$.subscribe(newState => {
      this.handleLoadingOverlay(newState);
    });

    merge(
      this.errorpageIsOpen$,
      this.sessionExpired$,
      this.constructionpageIsOpen$,
      this.userloggedout$,
      this.vnAddressInvalidPageOpen$,
    )
      .pipe(filter(Boolean))
      .subscribe(() => {
        this.spinnerIsActive = false;
        this.spinnerService.hide();
      });
  }

  ngOnDestroy(): void {
    this.appIsInitializedSubscription.unsubscribe();
  }

  triggerModalAction(buttonType: ModalButtonType): void {
    this.globalEventDispatcher.dispatch(
      this.globalActions.closeModal(ModalCloseReason[buttonType]),
    );
  }

  private handleLoadingOverlay(newState: boolean): void {
    if (newState === false) {
      this.spinnerIsActive = true;
      this.spinnerService.show(this.waitingpage);

      const SPINNER_TIMEOUT = 15000;
      setTimeout(() => {
        if (
          this.spinnerIsActive === true &&
          this.errorPageIsNotOpen() &&
          this.vnAddressInvalidPageNotOpen()
        ) {
          logAppInitializedConditions('Loading-Spinner', SPINNER_TIMEOUT);
        }
      }, SPINNER_TIMEOUT);
    } else if (newState === true) {
      this.spinnerIsActive = false;
      this.hideLoadingOverlayWithDelay();
    }
  }

  private errorPageIsNotOpen(): boolean {
    return !this.ngRedux.getState().transientState.errorPageOpen;
  }

  private vnAddressInvalidPageNotOpen(): boolean {
    return !this.ngRedux.getState().transientState.vnAddressInvalid.open;
  }

  private hideLoadingOverlayWithDelay(): void {
    setTimeout(() => {
      this.spinnerService.hide();
    }, 500);
  }
}
